import React, { useEffect, useState } from 'react'
import { makeStyles, Typography, useTheme } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import Page from '../../../components/page/page'
import {
  Collection,
  CollectionColumn,
  DataType,
  DecimalScale,
  Form,
  FormOptions,
  Section,
  SelectOption
} from '@globaleris/ui.library'
import { styles } from './ships.edit.styles'
import { EnumTipoOperacion, Operacion } from '../../../api'
import { enumKeyToValue, enumValueToKey } from '../../../helpers'
import { fetchOrigins, fetchTravel } from '../ships.service'
import { getCustomerDomain } from '../../../redux'
import { useHistory } from 'react-router-dom'
import ShipsModel from '../ships.model'
import {
  shipsSelectOptionMapper,
  destinationsSelectOptionMapper,
  commercialPlantSelectOptionMapper,
  ownerSelectOptionMapper,
  marketerSelectOptionMapper
} from '../ships.mapper'

export interface ShipsEditPageProps {}

export const ShipsEditPage: React.FC<any> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'shipsEdit', 'enums'])

  const [travelId] = useState<number>(props.location.state ? props.location.state.id : 83)
  const [travelSelected, setTravelSelected] = useState<ShipsModel | undefined>(undefined)
  const [ships, setShips] = useState<SelectOption[]>([])
  const [activeFilter, setActiveFilter] = useState<string | number>()
  const [origins, setOrigins] = useState<SelectOption[]>([])
  const [destinations, setDestinations] = useState<SelectOption[]>([])
  const [owners, setOwners] = useState<SelectOption[]>([])
  const [marketers, setMarketers] = useState<SelectOption[]>([])
  const [commercialPlants, setCommercialPlants] = useState<SelectOption[]>([])

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  let history = useHistory()

  useEffect(() => {
    const getTravel = async () => {
      await fetchTravel(travelId).then(response => {
        setActiveFilter(response.data.operationType)
        setTravelSelected(response.data)
        setShips(shipsSelectOptionMapper(response.data))
        setOwners(ownerSelectOptionMapper(response.data))
        setDestinations(destinationsSelectOptionMapper(response.data))
        setMarketers(marketerSelectOptionMapper(response.data))
        setCommercialPlants(commercialPlantSelectOptionMapper(response.data))
      })
    }

    getTravel()
  }, [travelId])

  useEffect(() => {
    const getOrigins = async (type: number | string) => {
      setOrigins(await fetchOrigins(type))
    }

    if (activeFilter) {
      getOrigins(activeFilter)
    }
  }, [activeFilter])

  //#region Form and Datatable Definitions

  const formSections: Section[] = [
    {
      inputs: [
        {
          name: 'tripDate',
          label: t('shipsEdit:form.inputs.date'),
          type: DataType.DATE,
          disablePast: true,
          required: true,
          readOnly: true
        },
        {
          name: 'ship.id',
          label: t('shipsEdit:form.inputs.ship'),
          type: DataType.SELECT,
          selectOptions: ships,
          // onChange: async data => {
          //   console.log('fetchShip', await fetchShip(data));
          //   const travelClone = structuredClone(travelSelected);
          //   travelClone.ship = await fetchShip(data);
          //   setTravelSelected(travelClone);
          // },
          required: true,
          readOnly: true
        },
        {
          name: 'ship.capacity',
          label: t('shipsEdit:form.inputs.shipCapacity'),
          type: DataType.NUMBER,
          decimalScale: DecimalScale.VOLUME,
          readOnly: true
          //defaultValue: shipCapacity
          //required: true
        },
        {
          name: 'operationType',
          label: t('shipsEdit:form.inputs.filter'),
          type: DataType.SELECT,
          selectOptions: [
            {
              value: EnumTipoOperacion.DescargaBuque,
              label: enumKeyToValue(EnumTipoOperacion, EnumTipoOperacion.DescargaBuque)
            },
            {
              value: EnumTipoOperacion.CargaBuque,
              label: enumKeyToValue(EnumTipoOperacion, EnumTipoOperacion.CargaBuque)
            }
          ],
          required: true,
          readOnly: true
        },
        {
          name: 'originPlant.id',
          label: t('shipsEdit:form.inputs.origin'),
          type: DataType.SELECT,
          selectOptions: origins,
          required: true,
          readOnly: true
        },
        {
          name: 'destinationsIds',
          label: t('shipsEdit:form.inputs.destination'),
          type: DataType.MULTISELECT,
          selectOptions: destinations,
          readOnly: true,
          required:
            enumValueToKey(EnumTipoOperacion, activeFilter) ===
            enumValueToKey(EnumTipoOperacion, EnumTipoOperacion.DescargaBuque)
              ? true
              : false
        },
        {
          name: 'quantity',
          label: t('shipsEdit:form.inputs.quantity'),
          type: DataType.NUMBER,
          decimalScale: DecimalScale.ENERGY,
          required: true,
          readOnly: true
          // onChange: data => {
          //   const travelClone = structuredClone(travelSelected);

          //   travelClone.operations = travelClone.operations.map((operacion: Operacion) => {
          //     return { ...operacion, cantidad: data };
          //   });

          //   setTravelSelected(travelClone);
          // }
        },
        {
          name: 'share',
          label: t('shipsEdit:form.inputs.share'),
          type: DataType.SWITCH,
          required: true,
          readOnly: true
        },
        {
          name: 'soe',
          label: t('shipsEdit:form.inputs.soe'),
          type: DataType.SWITCH,
          required: true,
          readOnly: true
        },
        {
          name: 'owner.id',
          label: t('shipsEdit:form.inputs.owner'),
          type: DataType.SELECT,
          readOnly: true,
          selectOptions: owners
        },
        {
          name: 'marketersIds',
          label: t('shipsEdit:form.inputs.marketers'),
          type: DataType.MULTISELECT,
          readOnly: true,
          selectOptions: marketers
        }
      ]
    }
  ]

  const formOptions: FormOptions = {
    elevation: 1,
    columns: 3,
    analytics: {
      customer: getCustomerDomain(),
      view: 'Ships Edit Form'
    },
    readOnly: true,
    onSave: async (data: any) => {
      history.goBack()
      return true
    }
  }

  const columnsForOperationsCollection: CollectionColumn[] = [
    {
      name: 'operationDate',
      label: t('shipsEdit:datatable.columns.date'),
      type: DataType.DATE,
      disablePast: true,
      required: true
    },
    {
      name: 'operationElemLogId',
      label: t('shipsEdit:datatable.columns.commercialPlant'),
      type: DataType.SELECT,
      required: true,
      selectOptions: commercialPlants
    },
    {
      name: 'operationQuantity',
      label: t('shipsEdit:datatable.columns.quantity'),
      type: DataType.NUMBER,
      decimalScale: DecimalScale.ENERGY,
      required: true
    },
    {
      name: 'operationSentQuantity',
      label: t('shipsEdit:datatable.columns.quantitySent'),
      type: DataType.NUMBER,
      decimalScale: DecimalScale.ENERGY,
      required: true
    },
    {
      name: 'operationTsoQuantity',
      label: t('shipsEdit:datatable.columns.quantityTSO'),
      type: DataType.NUMBER,
      decimalScale: DecimalScale.ENERGY,
      required: true
    }
  ]

  const optionsForOperationsCollection = {
    elevation: 2,
    canEdit: false,
    canDelete: false,
    canInsert: false,
    fabLabel: 'Nueva Operación'
  }

  const operationsCollection: Collection = {
    name: 'shipOperations',
    title: 'Operaciones',
    columns: columnsForOperationsCollection,
    options: optionsForOperationsCollection
  }

  const collections = [operationsCollection]

  //#endregion

  // //#region Handlers
  // const handlerSaveData = data => {
  //   modifyTravel(data);
  // };
  // //#endregion

  return (
    <Page>
      <div className={classes.appBar}>
        <Typography variant="h1" color="secondary" className={classes.title}>
          {t('shipsEdit:title')}
        </Typography>
      </div>
      <Form
        sections={formSections}
        data={travelSelected}
        options={formOptions}
        collections={collections}
      />
    </Page>
  )
}
